<template>
	<div class="cbd">
		<!-- La ficha es un expediente de una sola columna: cada grupo lleva su
		cabecera en el margen izquierdo (sticky) y adentro filas etiqueta/valor
		separadas por hairlines. Las acciones son píldoras que envuelven a su
		ancho natural: nada queda encajonado en columnas angostas. -->

		<!-- Empresa -->
		<section class="grp" aria-labelledby="grp-empresa">
			<h2 id="grp-empresa" class="grp__head">Empresa</h2>
			<dl class="grp__rows">
				<div class="row">
					<dt class="row__label">Fecha de inscripción</dt>
					<dd class="row__value">
						<span v-if="!isLoading" class="row__num">{{ moment(basicData.oldest_join_date).format('DD-MM-YYYY') }}</span>
						<span v-else class="row__muted">—</span>
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Usuarios</dt>
					<dd class="row__value">
						<div class="acts">
							<SimpleButton v-for="user in allUsers" :key="user.user_id" class="user_button"
								@click="() => addUser(user.user_id, user.user_email)">
								{{ user.user_email }}
							</SimpleButton>
							<SimpleButton class="user_button add_user_button" @click="() => addUser(-1, '')">
								Agregar usuario +
							</SimpleButton>
						</div>
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Logo</dt>
					<dd class="row__value">
						<div class="acts acts--center">
							<img :src="basicData.logo" class="company_logo" v-if="basicData.logo" alt="Logo de la empresa" />
							<span v-else class="logo-empty">Sin logo</span>
							<input type="file" ref="fileInput" accept="image/*" style="display: none;"
								@change="handleFileUpload" />
							<SimpleButton class="user_button add_user_button" @click="() => triggerFileDialog()">
								{{ basicData.logo ? 'Cambiar logo' : 'Subir logo' }}
							</SimpleButton>
							<SimpleButton v-if="basicData.logo" class="btn_danger"
								aria-label="Eliminar logo" @click="deleteImage()">
								Eliminar
							</SimpleButton>
						</div>
					</dd>
				</div>
			</dl>
		</section>

		<!-- Administración -->
		<section class="grp" aria-labelledby="grp-admin">
			<h2 id="grp-admin" class="grp__head">Administración</h2>
			<dl class="grp__rows">
				<div class="row">
					<dt class="row__label">Libro general</dt>
					<dd class="row__value">
						<div class="acts">
							<SimpleButton @click="() => openUpdateModalIdSheet()">
								Actualizar planilla
							</SimpleButton>
							<SimpleButton v-if="basicData.general_book_id_sheet" class="is-secondary"
								@click="() => goToSheet()">
								Abrir en Sheets ↗
							</SimpleButton>
						</div>
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Accesos</dt>
					<dd class="row__value">
						<div class="acts">
							<SimpleButton @click="() => openUpdateOperationsByUserModal()">
								Operaciones por usuario
							</SimpleButton>
							<SimpleButton @click="() => openUpdateUsernamePassModal()">
								Actualizar contraseña
							</SimpleButton>
						</div>
					</dd>
				</div>
			</dl>
		</section>

		<!-- Conexiones -->
		<section class="grp" aria-labelledby="grp-conex">
			<h2 id="grp-conex" class="grp__head">Conexiones</h2>
			<dl class="grp__rows">
				<!-- Estado de la conexión SII (basicData.sii, derivado en backend por
				sii_status_selector — mismo semáforo que el listado de la cartera). -->
				<div v-if="siiStatus" class="row">
					<dt class="row__label">Estado SII</dt>
					<dd class="row__value">
						<div class="sii" :class="'sii--' + siiStatus.tone">
							<span class="sii__dot" aria-hidden="true"></span>
							<div class="sii__body">
								<span class="sii__label">{{ siiStatus.label }}</span>
								<p class="sii__detail">{{ basicData.sii.detail }}</p>
								<p v-if="basicData.sii.used_at || basicData.sii.error_at" class="sii__meta">
									<span v-if="basicData.sii.used_at">Última sincronización {{ moment(basicData.sii.used_at).format('DD-MM-YYYY HH:mm') }}</span>
									<span v-if="basicData.sii.error_at">Último error {{ moment(basicData.sii.error_at).format('DD-MM-YYYY HH:mm') }}<template v-if="basicData.sii.num_errors"> ({{ basicData.sii.num_errors }} intentos)</template></span>
								</p>
							</div>
						</div>
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Terceros</dt>
					<dd class="row__value">
						<div class="acts">
							<SimpleButton class="act" @click="openGestionTerceros">
								<span class="act__text">Gestión de terceros</span>
								<span class="act__icon" aria-hidden="true">🔗</span>
							</SimpleButton>
						</div>
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Recargar desde Fintoc</dt>
					<dd class="row__value">
						<div class="acts">
							<SimpleButton class="act" @click="() => openModalReloadReport('sii')">
								<span class="act__text">Obligaciones SII</span>
								<span class="act__icon"><img :src="sii_logo" class="act__logo" alt="" /></span>
							</SimpleButton>
							<SimpleButton class="act" @click="() => openModalReloadReport('bank')">
								<span class="act__text">Movimientos bancarios</span>
								<span class="act__icon"><img :src="bank_logo" class="act__logo" alt="" /></span>
							</SimpleButton>
						</div>
					</dd>
				</div>
			</dl>
		</section>

		<!-- Reporte -->
		<section class="grp" aria-labelledby="grp-reporte">
			<h2 id="grp-reporte" class="grp__head">Reporte</h2>
			<dl class="grp__rows">
				<div class="row">
					<dt class="row__label">Precálculo</dt>
					<dd class="row__value">
						<ReportFactControl v-if="companyId" :company-id="companyId" />
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Inicio del reporte</dt>
					<dd class="row__value">
						<ReportStartMonthControl v-if="companyId" :company-id="companyId" />
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Exportar a Drive</dt>
					<dd class="row__value">
						<div class="acts">
							<SimpleButton class="act" :disabled="loadingReloadDrive"
								@click="() => sendObligationsToDrive('sii')">
								<span class="act__text">Obligaciones</span>
								<span class="act__icon"><img :src="sii_logo" class="act__logo" alt="" /></span>
							</SimpleButton>
							<SimpleButton class="act" :disabled="loadingReloadDrive"
								@click="() => sendObligationsToDrive('bank')">
								<span class="act__text">Movimientos</span>
								<span class="act__icon"><img :src="bank_logo" class="act__logo" alt="" /></span>
							</SimpleButton>
							<SimpleButton class="act" :disabled="loadingReloadDrive"
								@click="() => sendObligationsToDrive('classification')">
								<span class="act__text">Clasificaciones</span>
								<span class="act__icon"><img :src="sii_logo" class="act__logo" alt="" /></span>
							</SimpleButton>
						</div>
					</dd>
				</div>

				<div class="row">
					<dt class="row__label">Enviar por correo</dt>
					<dd class="row__value">
						<div class="acts">
							<SimpleButton class="act" @click="() => openEnviarCorreoTaxDocuments('sii')">
								<span class="act__text">Documentos SII</span>
								<span class="act__icon"><img :src="sii_logo" class="act__logo" alt="" /></span>
							</SimpleButton>
							<SimpleButton class="act" @click="() => openEnviarCorreoTaxDocuments('banks')">
								<span class="act__text">Documentos bancarios</span>
								<span class="act__icon"><img :src="bank_logo" class="act__logo" alt="" /></span>
							</SimpleButton>
						</div>
					</dd>
				</div>
			</dl>
		</section>
	</div>
</template>

<script>
import { mapActions } from "vuex";
import {
	downloadMovementsToSheet,
	downloadObligationsToSheet,
	exportClassificationsToSheet,
	getSheetExportStatus,
} from "@/api/downloadObligationsToSheet";
import { ImportRunPollTimeout, useImportRunPolling } from "@/composables/useImportRunPolling";
import AssignUsers from "@/components/modals/AssignUsers";
import ChangeOperationsByUsers from "@/components/modals/ChangeOperationsByUsers";
import ChangeUsernamePass from "@/components/modals/ChangeUsernamePass";
import IdSheetUpdate from "@/components/modals/IdSheetUpdate";
import GestionTerceros from "@/components/modals/GestionTerceros";
import ReloadFintocByCompany from "@/components/modals/ReloadFintocByCompany";
import SimpleButton from "@/components/modals/SimpleButton";
import ReportFactControl from "@/components/analyst/ReportFactControl";
import ReportStartMonthControl from "@/components/analyst/ReportStartMonthControl";
import UploadFileAsUserModal from "@/components/modals/UploadFileAsUserModal";
import { consoleHandlerError, notificate } from "@/utils/message";
import moment from "moment";

// Estados terminales de sheet_exports/status (el composable corta ahí).
const SHEET_EXPORT_TERMINAL_STATUSES = ["finished", "failed"];
// Botón → request que encola la exportación y nombre para la notificación.
const SHEET_EXPORTS = {
	sii: { request: downloadObligationsToSheet, label: "Obligaciones" },
	bank: { request: downloadMovementsToSheet, label: "Movimientos" },
	classification: { request: exportClassificationsToSheet, label: "Clasificaciones" },
};

export default {
	name: "CompanyInfoBasicData",
	components: {
		ReportFactControl,
		ReportStartMonthControl,
		SimpleButton,
	},
	props: {
		basicData: {
			type: Object,
			required: true,
			default: () => ({})
		}
	},
	setup() {
		// Las exportaciones a Drive corren en RQ: el POST responde 202 + job_id
		// y acá se sondea sheet_exports/status hasta finished/failed. 100
		// lecturas cada 3 s cubren la espera en cola más los 240 s del job.
		const { poll: pollSheetExport, stop: stopSheetExportPolling } = useImportRunPolling({
			terminalStatuses: SHEET_EXPORT_TERMINAL_STATUSES,
		});
		return { pollSheetExport, stopSheetExportPolling };
	},
	created() {
		const companyId = this.$route.query.companyId;
		this.companyId = companyId || null;
		this.companyRut = this.$route.query.companyRut;
		if (!companyId) {
			this.$router.push({ name: 'panel-analista' });
		}
		this.load_company_users();
	},
	data() {
		return {
			modalDefaultUpdateSheet: {
				parent: this,
				hasModalCard: true,
				customClass: "modal-class",
				canCancel: ["escape", "outside"],
			},
			allUsers: [],
			companyId: null,
			companyRut: '',
			loadingReloadDrive: false,
			isLoading: true,
		};
	},
	computed: {
		// Rutas root-relative: el publicPath anterior ("http://" + hostname)
		// forzaba http y rompía los assets bajo https.
		sii_logo: function () { return "/Logo_de_Servicio_de_Impuestos_Internos.png" },
		bank_logo: function () { return "/bank_logo.png" },
		siiStatus() {
			if (!this.basicData.sii) return null;
			const map = {
				OK: { label: "SII conectado", tone: "ok" },
				CONNECTING: { label: "SII conectando", tone: "progress" },
				LIKELY_BROKEN: { label: "SII a revisar", tone: "warn" },
				BROKEN: { label: "SII con problemas", tone: "bad" },
				NOT_APPLICABLE: { label: "Sin conexión SII", tone: "off" },
			};
			return map[this.basicData.sii.status] || map.NOT_APPLICABLE;
		},
	},
	methods: {
		...mapActions("analystCompany", [
			"deleteUploadedFile",
			"fetchUsersByCompany",
			"uploadCompanyFile",
		]),
		triggerFileDialog() {
			this.$refs.fileInput.click();
		},
		// Guard compartido de las acciones que necesitan usuarios: toast del
		// sistema en vez del alert() nativo del navegador.
		notificateNoUsers() {
			notificate(
				"Esta empresa no tiene usuarios todavía. Agrega uno desde \"Agregar usuario +\".",
				"is-warning is-light"
			);
		},
		async deleteImage() {
			if (!this.basicData.logo) return;

			try {
				const data = await this.deleteUploadedFile(this.basicData.companyId);

				if (data['success']) {
					this.basicData.logo = null
				}
				this.$emit("file-uploaded", data);
			} catch (error) {
				console.error("Error uploading file: ", error);
			}
		},
		async handleFileUpload(event) {
			const file = event.target.files[0];
			if (!file) return;

			try {
				const data = await this.uploadCompanyFile({
					companyId: this.basicData.companyId,
					file,
				});

				if (data['success']) {
					this.basicData.logo = data['image']
				}
				this.$emit("file-uploaded", data);
			} catch (error) {
				console.error("Error uploading file:", error);
			}
		},
		load_company_users() {
			const companyId = this.$route.query.companyId;
			this.fetchUsersByCompany(companyId).then((data) => {
				this.allUsers = data;
			})
		},
		addUser(user_id, user_email) {
			this.$buefy.modal.open({
				...this.modalDefaultUpdateSheet,
				component: AssignUsers,
				canCancel: ['escape', 'button', 'outside'],
				props: {
					user_id: user_id,
					user_email: user_email,
					company_id: this.basicData.companyId,
				},
				events: {
					close: () => {
						this.load_company_users();
					}
				},
			});
		},
		moment(date) {
			return moment(date);
		},
		openUpdateModalIdSheet() {
			this.$buefy.modal.open({
				...this.modalDefaultUpdateSheet,
				component: IdSheetUpdate,
				props: {
					companyId: this.basicData.companyId,
					general_book_id_sheet: this.basicData.general_book_id_sheet
				},
				events: {
					"update-id-sheet": (newId) => {
						this.basicData.general_book_id_sheet = newId
					},
				},
			});
		},
		openEnviarCorreoTaxDocuments(which) {
			let allUsersByFileId = this.allUsers;
			if (allUsersByFileId == null || allUsersByFileId.length == 0) {
				this.notificateNoUsers();
				return;
			}
			const message = which == 'sii' ? 'Enviar el correo con las obligaciones a' :
				'Enviar el correo con los movimientos bancarios a';
			this.$buefy.modal.open({
				...this.modalDefaultUpdateSheet,
				component: UploadFileAsUserModal,
				canCancel: ['escape', 'button', 'outside'],
				props: {
					idCompany: this.basicData.companyId,
					hideHistoricBtn: false,
					usersForEmail: this.allUsers,
					selectedFilesByUser: allUsersByFileId,
					noText: true,
					which: which,
					noTextMessage: message,
					sendMail: true,
				},
			});
		},
		openModalReloadReport(which) {
			this.$buefy.modal.open({
				...this.modalDefaultUpdateSheet,
				component: ReloadFintocByCompany,
				props: {
					rutCompany: this.companyRut,
					product: which,
				},
			});
		},
		openGestionTerceros() {
			this.$buefy.modal.open({
				...this.modalDefaultUpdateSheet,
				component: GestionTerceros,
				canCancel: ['escape', 'button', 'outside'],
				props: {
					companyId: this.basicData.companyId,
				},
			});
		},
		openUpdateOperationsByUserModal() {
			let allUsersByFileId = this.allUsers;
			if (allUsersByFileId == null || allUsersByFileId.length == 0) {
				this.notificateNoUsers();
				return;
			}
			this.$buefy.modal.open({
				...this.modalDefaultUpdateSheet,
				component: ChangeOperationsByUsers,
				canCancel: ['escape', 'button', 'outside'],
				props: {
					idCompany: this.basicData.companyId,
				},
			});
		},
		sendObligationsToDrive(which) {
			const { request: startExport, label } = SHEET_EXPORTS[which];

			this.loadingReloadDrive = true;
			return startExport({
				rut_company: this.companyRut,
			}).then(({ data }) => {
				// Sin `job_id` no hay nada que sondear (ninguna empresa, o varias
				// encoladas por un superusuario): el mensaje del backend alcanza.
				if (!data.job_id) {
					notificate(`<strong>Listo.</strong><br>${data.message}`, "is-info is-light");
					return null;
				}
				return this.pollSheetExport(() => getSheetExportStatus(data.job_id).then((res) => res.data))
					.then((status) => {
						if (status.status === "finished") {
							const notice = data.notice ? `<br>${data.notice}` : "";
							notificate(`<strong>Éxito!</strong><br>${label} exportadas a Drive.${notice}`, "is-success is-light");
						} else {
							notificate(`<strong>No se pudo exportar.</strong><br>${status.detail}`, "is-danger is-light");
						}
					});
			}).catch((error) => {
				if (error instanceof ImportRunPollTimeout) {
					notificate(
						`<strong>La exportación sigue en curso.</strong><br>Revisa el Drive en unos minutos.`,
						"is-warning is-light"
					);
					return;
				}
				consoleHandlerError({ error, empty: true });
			}).finally(() => {
				this.loadingReloadDrive = false;
			});
		},
		goToSheet() {
			let parcialUrl = 'https://docs.google.com/spreadsheets/d/' + this.basicData.general_book_id_sheet + '/view'
			window.open(parcialUrl, "_blank");
		},
		openUpdateUsernamePassModal() {
			let allUsersByFileId = this.allUsers;
			if (allUsersByFileId == null || allUsersByFileId.length == 0) {
				this.notificateNoUsers();
				return;
			}
			this.$buefy.modal.open({
				...this.modalDefaultUpdateSheet,
				component: ChangeUsernamePass,
				canCancel: ['escape', 'button', 'outside'],
				props: {
					idCompany: this.basicData.companyId,
					allUsers: this.allUsers,
				},
				events: {
					close: () => {
						this.fetchUsersByCompany(this.basicData.companyId).then((data) => {
							this.allUsers = data
						})
					}
				},
			});
		},
	},
	mounted() {
		if (this.basicData.oldest_join_date) {
			this.isLoading = false;
		}
	},
	watch: {
		'basicData.oldest_join_date': function (newVal) {
			if (newVal) {
				this.isLoading = false;
			}
		},
	},
};
</script>

<style scoped lang="scss">
// ============================================================================
// Ficha de empresa · Datos básicos. Un expediente de una sola columna:
//   [cabecera lateral sticky] | [etiqueta] [valor / acciones]
// Los tres breakpoints colapsan de afuera hacia adentro: primero la cabecera
// sube sobre su grupo (tablet), después la etiqueta sube sobre el valor
// (móvil). Nada tiene ancho fijo salvo las dos columnas de texto corto.
// ============================================================================
.cbd {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	--cbd-head-w: 132px;
	--cbd-label-w: 176px;
}

// ── Grupo: cabecera lateral + filas ──────────────────────────────────────
.grp {
	display: grid;
	grid-template-columns: var(--cbd-head-w) minmax(0, 1fr);
	column-gap: 28px;
	padding: 22px 0;
	border-top: 1px solid var(--ap-line-strong);

	&:first-child {
		padding-top: 4px;
		border-top: 0;
	}

	&:last-child {
		padding-bottom: 4px;
	}
}

.grp__head {
	margin: 0;
	padding-top: 12px;
	align-self: start;
	position: sticky;
	top: calc(var(--app-chrome-top, 56px) + 16px);
	font-size: 13px;
	font-weight: 700;
	line-height: 1.2;
	letter-spacing: -0.01em;
	color: var(--ap-ink);

	// Regla corta teal: la misma señal que marca la faceta activa del rail.
	&::after {
		content: "";
		display: block;
		width: 22px;
		height: 2px;
		margin-top: 8px;
		border-radius: 1px;
		background: var(--ap-teal);
	}
}

.grp__rows {
	margin: 0;
	min-width: 0;
}

// ── Fila etiqueta / valor ─────────────────────────────────────────────────
.row {
	display: grid;
	grid-template-columns: var(--cbd-label-w) minmax(0, 1fr);
	column-gap: 20px;
	align-items: start;
	padding: 12px 0;
	border-top: 1px solid var(--ap-line);

	&:first-child {
		border-top: 0;
	}
}

.row__label {
	padding-top: 9px; // alinea la línea base con el texto de las píldoras
	font-size: 11px;
	font-weight: 600;
	line-height: 1.35;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: var(--ap-muted);
}

.row__value {
	margin: 0;
	min-width: 0;
	font-size: 0.9rem;
	color: var(--ap-ink);
}

.row__num {
	display: inline-block;
	padding-top: 9px;
	font-variant-numeric: tabular-nums;
}

.row__muted {
	display: inline-block;
	padding-top: 9px;
	color: var(--ap-muted);
}

// ── Acciones: píldoras a ancho natural que envuelven ─────────────────────
.acts {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	min-width: 0;
}

.acts--center {
	align-items: center;
	gap: 8px 12px;
}

.company_logo {
	height: 32px;
	max-width: 160px;
	object-fit: contain;
}

.logo-empty {
	display: inline-flex;
	align-items: center;
	height: 32px;
	padding: 0 14px;
	border: 1px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	font-size: 12px;
	color: var(--ap-muted);
}

// ── Estado de la conexión SII ─────────────────────────────────────────────
.sii {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	max-width: 560px;
	padding: 10px 14px 11px;
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	background: var(--ap-paper);
}

.sii__dot {
	flex-shrink: 0;
	width: 8px;
	height: 8px;
	margin-top: 6px;
	border-radius: 50%;
	background: var(--ap-muted);
}

.sii__body {
	min-width: 0;
}

.sii__label {
	font-size: 13px;
	font-weight: 600;
	color: var(--ap-ink);
}

.sii__detail {
	margin: 3px 0 0;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-slate);
	overflow-wrap: anywhere;
}

.sii__meta {
	display: flex;
	flex-wrap: wrap;
	gap: 2px 16px;
	margin: 6px 0 0;
	font-size: 11.5px;
	color: var(--ap-muted);
	font-variant-numeric: tabular-nums;
}

.sii--ok {
	border-color: rgba(15, 157, 107, 0.22);

	.sii__dot {
		background: var(--ap-ok);
	}
}

.sii--progress {
	border-color: rgba(13, 125, 128, 0.25);

	.sii__dot {
		background: var(--ap-teal);
	}
}

.sii--warn {
	border-color: rgba(200, 138, 0, 0.25);
	background: var(--ap-warn-wash);

	.sii__dot {
		background: var(--ap-warn);
	}
}

.sii--bad {
	border-color: rgba(192, 69, 59, 0.28);
	background: var(--ap-alert-wash);

	.sii__dot {
		background: var(--ap-alert);
	}
}

// ── Píldoras con icono en slot fijo ───────────────────────────────────────
.act__text {
	text-align: left;
}

.act__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	flex-shrink: 0;
	font-size: 13px;
}

.act__logo {
	max-height: 15px;
	max-width: 22px;
	object-fit: contain;
}

// ── Theming de SimpleButton (chrome externo, no editable) ─────────────────
// SimpleButton no se puede tocar; lo alineamos al tema del analista vía
// :deep sobre su clase raíz .simple_button. Píldora secundaria por defecto,
// a ancho natural y con texto que envuelve si el espacio no alcanza.
:deep(.simple_button){
	display: inline-flex;
	align-items: center;
	justify-content: flex-start;
	gap: 10px;
	max-width: 100%;
	margin: 0;
	padding: 8px 16px;
	font-family: var(--ap-font);
	font-size: 0.85rem;
	font-weight: 500;
	line-height: 1.25;
	text-align: left;
	white-space: normal;
	color: var(--ap-ink-2);
	background: var(--ap-paper);
	background-image: none;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	cursor: pointer;
	filter: none;
	transition: color 0.16s var(--ap-ease), border-color 0.16s var(--ap-ease),
		background-color 0.16s var(--ap-ease), transform 0.16s var(--ap-ease);

	&:hover {
		padding: 8px 16px;
		color: var(--ap-teal-deep);
		background: var(--ap-paper);
		border-color: var(--ap-teal);
		transform: translateY(-1px);
		filter: none;
	}

	&:active {
		background: var(--ap-teal-wash);
		transform: translateY(0);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

// Chips de usuario: superficie tenue, discretos; el correo puede cortarse.
:deep(.simple_button.user_button){
	padding: 6px 14px;
	font-size: 0.8rem;
	color: var(--ap-slate);
	background: var(--ap-mist);
	border-color: var(--ap-line);
	overflow-wrap: anywhere;

	&:hover {
		padding: 6px 14px;
		color: var(--ap-teal-deep);
		background: var(--ap-teal-wash);
		border-color: var(--ap-teal);
	}
}

// Acción de agregar / subir: acento teal suave.
:deep(.simple_button.add_user_button){
	color: var(--ap-teal-deep);
	background: var(--ap-teal-wash);
	border-color: var(--ap-teal);

	&:hover {
		background: var(--ap-teal-wash);
		border-color: var(--ap-teal-deep);
	}
}

// Variante secundaria (abrir la planilla).
:deep(.simple_button.is-secondary){
	color: var(--ap-ink-2);
	background: var(--ap-paper);
}

// Destructivo (eliminar logo).
:deep(.simple_button.btn_danger){
	padding: 6px 14px;
	font-size: 0.8rem;
	color: var(--ap-alert);
	background: var(--ap-alert-wash);
	border-color: rgba(192, 69, 59, 0.28);

	&:hover {
		padding: 6px 14px;
		color: var(--ap-alert);
		background: var(--ap-alert-wash);
		border-color: rgba(192, 69, 59, 0.5);
	}
}

// Deshabilitado (exports a Drive en curso).
:deep(.simple_button:disabled){
	opacity: 0.55;
	cursor: wait;
	transform: none;

	&:hover {
		color: var(--ap-ink-2);
		border-color: var(--ap-line-strong);
		transform: none;
	}
}

// ── Breakpoints ───────────────────────────────────────────────────────────
// Tablet / panel angosto: la cabecera lateral sube sobre su grupo.
@media (max-width: 1080px) {
	.grp {
		grid-template-columns: minmax(0, 1fr);
		row-gap: 6px;
		padding: 18px 0;
	}

	.grp__head {
		position: static;
		padding-top: 0;
	}
}

// Móvil: la etiqueta sube sobre el valor; las píldoras ocupan el ancho.
@media (max-width: 640px) {
	.cbd {
		--cbd-label-w: 100%;
	}

	.row {
		grid-template-columns: minmax(0, 1fr);
		row-gap: 8px;
		padding: 12px 0;
	}

	.row__label,
	.row__num,
	.row__muted {
		padding-top: 0;
	}

	.acts > :deep(.simple_button.act) {
		width: 100%;
		justify-content: space-between;
	}

	.sii {
		max-width: none;
	}
}

@media (prefers-reduced-motion: reduce) {
	:deep(.simple_button){
		transition: none;

		&:hover {
			transform: none;
		}
	}
}
</style>
